import { resetSystemHistories } from '../services/system_history_service.mjs';
import { ensureColorCatalogsForProject } from '../../../colors/color_catalog_registry.js?v=20260720a';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import {
  canonicalCoreGroupKey,
  getCoreGroups,
  getSingleActiveCoreGroup
} from '../objects/group_service.js?v=20260904c';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { markProjectModelChanged } from './project_revision_service.js';
import {
  PROJECT_STATE_MODES,
  normalizeProjectState,
  resetTransientProjectRuntimeState,
  syncProjectItems
} from './project_state_service.js?v=20260904f';
import {
  hasDeclaredProjectDrawingContent,
  hasProjectDrawingContent,
  rememberProjectDrawingContentManifest
} from './project_content_registry.mjs?v=20260904f';
import {
  copyCoreGroupTitles,
  reconcileCoreGroupTitles,
  updateCoreGroupWorkflowStatus,
  updateCoreGroupTitleSuffix
} from '../objects/core_group_title_model.mjs?v=20260904c';
import {
  cloneJsonValue,
  createProjectDocument,
  parseProjectDocument
} from './project_document.mjs';
import {
  cloneDrawingSheetValue,
  defaultDrawingSheetIdFactory,
  DRAWING_SHEETS_SCHEMA_VERSION,
  normalizeDrawingSheetDocument,
  normalizeDrawingSheetMetadata,
  normalizeDrawingSheetSidebarGroups,
  normalizeDrawingSheets,
  suggestedDrawingSheetTitle,
  validateDrawingSheetTitle
} from './drawing_sheet_document.mjs?v=20260904c';
import {
  hideWorkspaceLoadingOverlay,
  showWorkspaceLoadingOverlay
} from '../ui/workspace_loading_overlay.js?v=20260826a';

const MAX_DRAWING_SHEETS = 100;
const DEFAULT_CAMERA_2D = Object.freeze({ x: 0, z: 0, scale: 0.25 });
const SHARED_ORDER_PROJECT_KEYS = Object.freeze([
  'name',
  'systemType',
  'projectNumber',
  'customerName',
  'customerId',
  'customerIdentityName',
  'customerContactId',
  'customerContactFirstName',
  'customerContactLastName',
  'customerContactName',
  'orderNumber',
  'orderDescription',
  'seller',
  'sellerUserId',
  'avor',
  'avorUserId',
  'persistence',
  'inputDrafts',
  // Ownergebundene Plugin-Einstellungen gelten auf allen Ebenen.
  'systemOrderData',
  // Farbrevisionen gelten für den ganzen Auftrag (color-catalog-revisions.md, F2).
  'colorCatalogBinding'
]);

const runtimeHistoryBySheetId = new Map();
let sheetSwitchInProgress = false;

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cloneValue(value, fallback = null) {
  if (value === undefined) return fallback;
  return cloneDrawingSheetValue(value);
}

function currentNavigationState() {
  return {
    activeCompanyId: SystemRegistry.state?.activeCompanyId || null,
    activeCategoryId: SystemRegistry.state?.activeCategoryId || null,
    activeSystemId: SystemRegistry.state?.activeSystemId || null
  };
}

function createCurrentDrawingDocument() {
  syncProjectItems();
  return createProjectDocument({
    project: S.project,
    camera2D: S.camera2D,
    ...currentNavigationState()
  });
}

function sharedOrderProjectState(project = S.project) {
  const source = isPlainObject(project) ? project : {};
  const shared = {};
  for (const key of SHARED_ORDER_PROJECT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(source, key)) shared[key] = cloneValue(source[key]);
  }
  // Auch eine fehlende Farbbindung (Erstbestand, z. B. nach Undo) muss eine
  // ältere Kopie in der Zielebene ersetzen.
  if (!Object.prototype.hasOwnProperty.call(shared, 'colorCatalogBinding')) shared.colorCatalogBinding = null;
  return shared;
}

function mergeSharedOrderProjectState(project, shared) {
  return {
    ...(isPlainObject(project) ? cloneValue(project, {}) : {}),
    ...(isPlainObject(shared) ? cloneValue(shared, {}) : {})
  };
}

function blankDrawingProject(source = S.project) {
  const project = isPlainObject(source) ? source : {};
  return mergeSharedOrderProjectState({
    name: project.name || 'GMS Planner Projekt',
    systemType: project.systemType || 'multi_system',
    gridColor: project.gridColor,
    gridSize: project.gridSize,
    helperGridSize: project.helperGridSize,
    showHelperGrid: project.showHelperGrid === true,
    snap: project.snap !== false,
    showGrid: project.showGrid !== false,
    currentView: 'plan',
    planLayerVisibility: { schemaVersion: 1, groups: {}, layers: {} },
    levels: cloneValue(project.levels, undefined),
    floorplan: null,
    dimensions: [],
    areaMeasurements: [],
    textAnnotations: [],
    obstacles: [],
    objectAssets: [],
    items: [],
    bomOverrides: { manualItems: [], order: [] }
  }, sharedOrderProjectState(project));
}

function activeSheetFromEnvelope(envelope = S.drawingSheets) {
  if (!isPlainObject(envelope) || !Array.isArray(envelope.sheets)) return null;
  return envelope.sheets.find(sheet => sheet?.id === envelope.activeSheetId) || null;
}

function drawingSheetsEnvelopeLooksUsable(value) {
  if (!isPlainObject(value)
    || Number(value.schemaVersion) !== DRAWING_SHEETS_SCHEMA_VERSION
    || !Array.isArray(value.sheets)
    || value.sheets.length === 0
    || value.sheets.length > MAX_DRAWING_SHEETS) return false;
  const ids = new Set();
  const numbers = new Set();
  let activeCount = 0;
  for (const sheet of value.sheets) {
    if (!isPlainObject(sheet) || !sheet.id || ids.has(sheet.id)) return false;
    const number = Number(sheet.number);
    if (!Number.isSafeInteger(number) || number <= 0 || numbers.has(number)) return false;
    ids.add(sheet.id);
    numbers.add(number);
    if (sheet.id === value.activeSheetId && sheet.state === null) activeCount += 1;
  }
  const nextNumber = Number(value.nextDrawingSheetNumber);
  const highestNumber = Math.max(...numbers);
  return activeCount === 1
    && Number.isSafeInteger(nextNumber)
    && nextNumber > highestNumber;
}

function ensureDrawingSheets(options = {}) {
  if (drawingSheetsEnvelopeLooksUsable(S.drawingSheets)) return S.drawingSheets;
  const normalized = normalizeDrawingSheets(S.drawingSheets, options);
  S.drawingSheets = normalized;
  return normalized;
}

function restoreDrawingSheetsFromDocument(document, options = {}) {
  const normalized = normalizeDrawingSheetDocument(document, options);
  rememberProjectDrawingContentManifest(normalized.project, normalized.drawingContent);
  S.drawingSheets = normalized.drawingSheets;
  runtimeHistoryBySheetId.clear();
  resetSystemHistories();
  return normalized;
}

function resetDrawingSheets(options = {}) {
  runtimeHistoryBySheetId.clear();
  resetSystemHistories();
  S.drawingSheets = normalizeDrawingSheets(null, options);
  return S.drawingSheets;
}

function summarizeSidebarGroups(groups = getCoreGroups()) {
  return normalizeDrawingSheetSidebarGroups(groups);
}

function reconcileDrawingSheetCoreGroups(sheet, groups, envelope = ensureDrawingSheets()) {
  if (!sheet) return { groups: [], changed: false };
  const reconciled = reconcileCoreGroupTitles(
    sheet.coreGroupTitles,
    groups,
    envelope.nextCoreGroupNumber
  );
  sheet.coreGroupTitles = reconciled.entries;
  envelope.nextCoreGroupNumber = reconciled.nextNumber;
  return reconciled;
}

function activeDrawingSheetCoreGroups(groups = getCoreGroups(), options = {}) {
  const envelope = ensureDrawingSheets();
  const activeSheet = activeSheetFromEnvelope(envelope);
  if (!activeSheet) return [];
  const reconciled = reconcileDrawingSheetCoreGroups(activeSheet, groups, envelope);
  const titledGroups = reconciled.groups.map(group => ({
    ...group,
    drawingSheetId: activeSheet.id
  }));
  activeSheet.sidebarGroups = summarizeSidebarGroups(titledGroups);
  S.drawingSheets = envelope;
  if (reconciled.changed && options.markChanged !== false) {
    markDrawingSheetsChanged('core-group-title-numbering');
  }
  return titledGroups;
}

function syncActiveDrawingSheetSidebarGroups(groups = getCoreGroups(), options = {}) {
  const titledGroups = activeDrawingSheetCoreGroups(groups, options);
  return summarizeSidebarGroups(titledGroups);
}

function serializeDrawingSheets() {
  syncActiveDrawingSheetSidebarGroups();
  const normalized = normalizeDrawingSheets(S.drawingSheets);
  const activeSheet = activeSheetFromEnvelope(normalized);
  if (activeSheet) activeSheet.state = null;
  S.drawingSheets = normalized;
  return cloneJsonValue(normalized);
}

function drawingSheetNavigationModel() {
  const envelope = ensureDrawingSheets();
  const activeGroups = activeDrawingSheetCoreGroups(getCoreGroups(), { markChanged: true });
  let inactiveTitlesChanged = false;
  for (const sheet of envelope.sheets) {
    if (sheet.id === envelope.activeSheetId) continue;
    const reconciled = reconcileDrawingSheetCoreGroups(sheet, sheet.sidebarGroups, envelope);
    sheet.sidebarGroups = summarizeSidebarGroups(reconciled.groups);
    inactiveTitlesChanged ||= reconciled.changed;
  }
  if (inactiveTitlesChanged) markDrawingSheetsChanged('core-group-title-numbering');
  const activeSheet = activeSheetFromEnvelope(envelope);
  if (activeSheet) activeSheet.sidebarGroups = summarizeSidebarGroups(activeGroups);
  S.drawingSheets = envelope;
  return {
    activeSheetId: envelope.activeSheetId,
    sheets: envelope.sheets.map(sheet => ({
      id: sheet.id,
      number: sheet.number,
      title: sheet.title,
      sortOrder: sheet.sortOrder,
      active: sheet.id === envelope.activeSheetId,
      sidebarGroups: cloneValue(sheet.sidebarGroups, [])
    }))
  };
}

function activeDrawingSheet() {
  const sheet = activeSheetFromEnvelope(ensureDrawingSheets());
  return sheet ? {
    id: sheet.id,
    number: sheet.number,
    title: sheet.title,
    sortOrder: sheet.sortOrder,
    sidebarGroups: cloneValue(sheet.sidebarGroups, [])
  } : null;
}

function activeDrawingSheetCoreGroup(group = {}) {
  const groupId = String(group?.groupId || group?.id || '').trim();
  const systemId = String(group?.systemId || group?.systemKey || '').trim();
  const key = canonicalCoreGroupKey(systemId, groupId);
  if (!key) return null;
  return activeDrawingSheetCoreGroups().find(candidate => (
    String(candidate.systemId || candidate.systemKey || '') === systemId
    && String(candidate.groupId || candidate.id || '') === groupId
    && String(candidate.key || '') === key
  )) || null;
}

function activeDrawingSheetPositionPrefix(systemId, memberId) {
  const normalizedSystemId = String(systemId || '').trim();
  const normalizedMemberId = String(memberId || '').trim();
  if (!normalizedSystemId || !normalizedMemberId) return null;
  const envelope = drawingSheetsEnvelopeLooksUsable(S.drawingSheets)
    ? S.drawingSheets
    : normalizeDrawingSheets(S.drawingSheets);
  const sheet = activeSheetFromEnvelope(envelope);
  if (!sheet?.number) return null;
  const groups = reconcileCoreGroupTitles(sheet.coreGroupTitles, getCoreGroups(), envelope.nextCoreGroupNumber).groups;
  const group = groups.find(candidate => (
    String(candidate.systemId || candidate.systemKey || '') === normalizedSystemId
    && [
      ...(candidate.memberIds || []),
      ...(candidate.objectIds || []),
      ...(candidate.bomIds || []),
      ...(candidate.renderIds || []),
      candidate.primaryObjectId,
      candidate.sourceId
    ].some(id => String(id || '') === normalizedMemberId)
  ));
  if (!Number.isSafeInteger(Number(group?.groupNumber)) || Number(group.groupNumber) <= 0) return null;
  return {
    drawingSheetNumber: Number(sheet.number),
    groupNumber: Number(group.groupNumber),
    drawingSheetId: sheet.id,
    groupKey: group.key || ''
  };
}

function storedProjectForSheet(sheet) {
  if (!sheet) return null;
  if (sheet.id === S.drawingSheets?.activeSheetId) return S.project;
  return isPlainObject(sheet.state?.project) ? sheet.state.project : null;
}

// Lesender Blick auf alle Ebenen des Auftrags (aktive Ebene = S.project), z. B.
// für den Aktualisieren-Hinweis (F7). Keine Kopien, nicht verändern.
function drawingSheetProjects() {
  const envelope = drawingSheetsEnvelopeLooksUsable(S.drawingSheets) ? S.drawingSheets : null;
  if (!envelope) return isPlainObject(S.project) ? [{ id: '', number: 1, title: '', active: true, project: S.project }] : [];
  return envelope.sheets
    .map(sheet => ({ id: sheet.id, number: sheet.number, title: sheet.title || '', active: sheet.id === envelope.activeSheetId,
      project: storedProjectForSheet(sheet) }))
    .filter(entry => entry.project);
}

function drawingSheetHasContent(sheetId) {
  const envelope = ensureDrawingSheets();
  const sheet = envelope.sheets.find(entry => entry.id === sheetId);
  const project = storedProjectForSheet(sheet);
  if (!project) return false;
  if (hasProjectDrawingContent(project)) return true;
  return sheet?.id !== envelope.activeSheetId
    && hasDeclaredProjectDrawingContent(project, sheet?.state?.drawingContent);
}

function anyDrawingSheetHasContent() {
  const envelope = ensureDrawingSheets();
  return envelope.sheets.some(sheet => {
    const project = storedProjectForSheet(sheet);
    if (!project) return false;
    if (hasProjectDrawingContent(project)) return true;
    return sheet.id !== envelope.activeSheetId
      && hasDeclaredProjectDrawingContent(project, sheet?.state?.drawingContent);
  });
}

function cloneHistoryState(history = S.history) {
  const source = isPlainObject(history) ? history : {};
  return {
    systems: cloneValue(source.systems || {}, {}),
    undo: cloneValue(Array.isArray(source.undo) ? source.undo : [], []),
    redo: cloneValue(Array.isArray(source.redo) ? source.redo : [], []),
    limit: Number.isSafeInteger(Number(source.limit)) ? Number(source.limit) : 80
  };
}

function storeActiveSheetHistory(sheetId) {
  if (sheetId) runtimeHistoryBySheetId.set(sheetId, cloneHistoryState());
}

function restoreSheetHistory(sheetId) {
  const orderData = S.history?.orderData || {};
  const stored = runtimeHistoryBySheetId.get(sheetId);
  if (stored) {
    // Store already detached this bank from all previously active references.
    // Consume it so the mutable active history has exactly one owner.
    runtimeHistoryBySheetId.delete(sheetId);
    S.history = { ...stored, orderData };
    return;
  }
  S.history = { ...cloneHistoryState({ undo: [], redo: [], limit: S.history?.limit || 80 }), orderData };
}

function applyNavigationState(document) {
  if (document.activeCompanyId && SystemRegistry.getCompany(document.activeCompanyId)) {
    SystemRegistry.state.activeCompanyId = document.activeCompanyId;
  }
  if (document.activeCategoryId && SystemRegistry.getCategory(document.activeCategoryId)) {
    const category = SystemRegistry.getCategory(document.activeCategoryId);
    SystemRegistry.state.activeCategoryId = category.id;
    if (category.companyId) SystemRegistry.state.activeCompanyId = category.companyId;
  }
  if (document.activeSystemId && SystemRegistry.getSystem(document.activeSystemId)) {
    const system = SystemRegistry.getSystem(document.activeSystemId);
    SystemRegistry.state.activeSystemId = system.id;
    if (system.categoryId) SystemRegistry.state.activeCategoryId = system.categoryId;
    if (system.companyId) SystemRegistry.state.activeCompanyId = system.companyId;
  }
}

function markDrawingSheetsChanged(reason) {
  if (!S.file) S.file = { name: '', dirty: true };
  S.file.dirty = true;
  markProjectModelChanged(reason);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ks:project-dirty', { detail: { label: reason } }));
  }
}

function afterNextPaint() {
  if (typeof requestAnimationFrame !== 'function') return Promise.resolve();
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

async function activateDrawingSheet(sheetId, options = {}) {
  if (sheetSwitchInProgress) return false;
  const envelope = ensureDrawingSheets();
  const targetSheet = envelope.sheets.find(sheet => sheet.id === sheetId);
  if (!targetSheet) throw new Error('Zeichnungsebene wurde nicht gefunden.');
  const currentSheet = activeSheetFromEnvelope(envelope);
  if (currentSheet?.id === targetSheet.id) {
    if (options.clearSelection !== false) AppRuntime.clearGlobalSelection();
    AppRuntime.refresh(false);
    return true;
  }
  if (!isPlainObject(targetSheet.state?.project)) {
    throw new Error('Zeichnungsebene enthält keinen gültigen Zeichnungszustand.');
  }

  sheetSwitchInProgress = true;
  const loadingToken = options.showLoadingOverlay === false
    ? null
    : showWorkspaceLoadingOverlay({
      label: t('modal.zeichnungsebene.wert.wird_geladen', 'Zeichnungsebene wird geladen...'),
      details: targetSheet.title
    });
  try {
    await afterNextPaint();
    const shared = sharedOrderProjectState(S.project);
    const targetDocument = parseProjectDocument(targetSheet.state);
    // Parsing detached the project; shared values were copied from the active
    // order above. Keep the camera detached before the asynchronous preload too.
    const targetProject = { ...targetDocument.project, ...shared };
    if (!Object.hasOwn(shared, 'systemOrderData')) delete targetProject.systemOrderData;
    const targetCamera = cloneValue(targetDocument.camera2D, DEFAULT_CAMERA_2D);
    rememberProjectDrawingContentManifest(targetProject, targetDocument.drawingContent);

    // Lazy-Kataloge und -Systeme werden vor jeder Mutation des laufenden
    // Auftrags geladen. Scheitert dieser Schritt, bleibt die bisherige Ebene
    // inklusive Auswahl, Historie und aktivem Zustand vollständig erhalten.
    await ensureColorCatalogsForProject(targetProject);
    await SystemRegistry.ensureSystemsForProject(targetProject, {
      activeSystemId: targetDocument.activeSystemId
    });

    if (options.captureCurrent !== false && currentSheet) {
      syncActiveDrawingSheetSidebarGroups();
      currentSheet.state = createCurrentDrawingDocument();
      delete currentSheet.state.project.systemOrderData;
      storeActiveSheetHistory(currentSheet.id);
    }

    resetTransientProjectRuntimeState();
    S.project = targetProject;
    S.camera2D = targetCamera;
    envelope.activeSheetId = targetSheet.id;
    targetSheet.state = null;
    S.drawingSheets = envelope;
    restoreSheetHistory(targetSheet.id);

    applyNavigationState(targetDocument);
    const stateMode = options.initialProjectStateMode === PROJECT_STATE_MODES.CREATE
      ? PROJECT_STATE_MODES.CREATE
      : PROJECT_STATE_MODES.HYDRATE;
    normalizeProjectState({ mode: stateMode, source: 'drawing-sheet-activation' });
    SystemRegistry.normalizeNavigationState();
    SystemRegistry.renderShell();
    syncActiveDrawingSheetSidebarGroups();
    if (options.markDirty !== false) {
      markDrawingSheetsChanged(options.changeReason || 'drawing-sheet-switch');
    }
    AppRuntime.refresh(false);
    await afterNextPaint();
    return true;
  } finally {
    if (loadingToken !== null) hideWorkspaceLoadingOverlay(loadingToken);
    sheetSwitchInProgress = false;
  }
}

function drawingDocumentForCopy(sourceSheet) {
  if (!sourceSheet) return null;
  if (sourceSheet.id === S.drawingSheets?.activeSheetId) {
    syncActiveDrawingSheetSidebarGroups();
    return createCurrentDrawingDocument();
  }
  return sourceSheet.state ? cloneValue(sourceSheet.state) : null;
}

async function createDrawingSheet(options = {}) {
  const envelope = ensureDrawingSheets();
  if (envelope.sheets.length >= MAX_DRAWING_SHEETS) {
    throw new Error(`Ein Auftrag kann höchstens ${MAX_DRAWING_SHEETS} Zeichnungsebenen enthalten.`);
  }
  const titleValidation = validateDrawingSheetTitle(
    options.title || suggestedDrawingSheetTitle(envelope)
  );
  if (!titleValidation.valid) throw new Error(titleValidation.error);
  const mode = options.mode === 'copy' ? 'copy' : 'empty';
  let state;
  let sidebarGroups = [];
  let coreGroupTitles = [];
  if (mode === 'copy') {
    const sourceSheet = envelope.sheets.find(sheet => sheet.id === options.sourceSheetId);
    if (!sourceSheet) throw new Error('Bitte eine vorhandene Zeichnungsebene zum Kopieren auswählen.');
    state = drawingDocumentForCopy(sourceSheet);
    sidebarGroups = sourceSheet.id === envelope.activeSheetId
      ? summarizeSidebarGroups()
      : cloneValue(sourceSheet.sidebarGroups, []);
    const sourceTitles = reconcileDrawingSheetCoreGroups(sourceSheet, sidebarGroups, envelope);
    sourceSheet.sidebarGroups = summarizeSidebarGroups(sourceTitles.groups);
    const copiedTitles = copyCoreGroupTitles(
      sourceSheet.coreGroupTitles,
      sourceSheet.sidebarGroups,
      envelope.nextCoreGroupNumber
    );
    coreGroupTitles = copiedTitles.entries;
    sidebarGroups = summarizeSidebarGroups(copiedTitles.groups);
    envelope.nextCoreGroupNumber = copiedTitles.nextNumber;
  } else {
    state = createProjectDocument({
      project: blankDrawingProject(),
      camera2D: DEFAULT_CAMERA_2D,
      ...currentNavigationState()
    });
  }
  if (!state) throw new Error('Zeichnungsebene konnte nicht vorbereitet werden.');

  const usedIds = new Set(envelope.sheets.map(sheet => sheet.id));
  const nextDrawingSheetNumber = envelope.nextDrawingSheetNumber;
  const newSheet = normalizeDrawingSheetMetadata({
    id: defaultDrawingSheetIdFactory(),
    number: nextDrawingSheetNumber,
    title: titleValidation.title,
    sortOrder: envelope.sheets.length,
    state,
    sidebarGroups,
    coreGroupTitles
  }, {
    active: false,
    index: envelope.sheets.length,
    usedIds
  });
  envelope.sheets.push(newSheet);
  envelope.nextDrawingSheetNumber = nextDrawingSheetNumber + 1;
  S.drawingSheets = envelope;
  try {
    const activated = await activateDrawingSheet(newSheet.id, {
      changeReason: mode === 'copy' ? 'drawing-sheet-copy' : 'drawing-sheet-create',
      initialProjectStateMode: mode === 'empty'
        ? PROJECT_STATE_MODES.CREATE
        : PROJECT_STATE_MODES.HYDRATE
    });
    if (!activated) throw new Error('Zeichnungsebene konnte nicht aktiviert werden.');
    return activeDrawingSheet();
  } catch (error) {
    envelope.sheets = envelope.sheets.filter(sheet => sheet.id !== newSheet.id);
    envelope.nextDrawingSheetNumber = nextDrawingSheetNumber;
    throw error;
  }
}

function renameDrawingSheet(sheetId, title) {
  const envelope = ensureDrawingSheets();
  const sheet = envelope.sheets.find(entry => entry.id === sheetId);
  if (!sheet) throw new Error('Zeichnungsebene wurde nicht gefunden.');
  const validation = validateDrawingSheetTitle(title);
  if (!validation.valid) throw new Error(validation.error);
  if (sheet.title === validation.title) return sheet.title;
  sheet.title = validation.title;
  markDrawingSheetsChanged('drawing-sheet-rename');
  AppRuntime.refreshChromeOnly('drawing-sheet-rename');
  return sheet.title;
}

function renameActiveCoreGroupTitleSuffix(group, suffix) {
  const envelope = ensureDrawingSheets();
  const sheet = activeSheetFromEnvelope(envelope);
  if (!sheet) throw new Error('Zeichnungsebene wurde nicht gefunden.');
  const titledGroup = activeDrawingSheetCoreGroup(group);
  if (!titledGroup?.groupTitleEntryId) {
    throw new Error('Die ausgewählte Gruppe wurde nicht gefunden.');
  }
  const updated = updateCoreGroupTitleSuffix(
    sheet.coreGroupTitles,
    titledGroup.groupTitleEntryId,
    suffix
  );
  sheet.coreGroupTitles = updated.entries;
  const reconciled = reconcileDrawingSheetCoreGroups(sheet, getCoreGroups(), envelope);
  sheet.sidebarGroups = summarizeSidebarGroups(reconciled.groups);
  S.drawingSheets = envelope;
  markDrawingSheetsChanged('core-group-title-rename');
  AppRuntime.refreshChromeOnly('core-group-title-rename');
  return updated.displayName;
}

function setActiveCoreGroupWorkflowStatus(group, workflowStatus) {
  const envelope = ensureDrawingSheets();
  const sheet = activeSheetFromEnvelope(envelope);
  if (!sheet) throw new Error('Zeichnungsebene wurde nicht gefunden.');

  // Vor der Mutation nochmals gegen die aktuelle Gruppenmenge auflösen. So
  // kann ein veraltetes Select-Event nach Ebenen-/Auswahlwechsel nie eine
  // andere Gruppe über einen inzwischen wiederverwendeten Owner-Key ändern.
  const expectedSheetId = String(group?.drawingSheetId || '').trim();
  const expectedEntryId = String(group?.groupTitleEntryId || '').trim();
  const expectedSystemId = String(group?.systemId || group?.systemKey || '').trim();
  const expectedGroupId = String(group?.groupId || group?.id || '').trim();
  const expectedKey = canonicalCoreGroupKey(expectedSystemId, expectedGroupId);
  const titledGroup = activeDrawingSheetCoreGroup(getSingleActiveCoreGroup());
  const actualSystemId = String(titledGroup?.systemId || titledGroup?.systemKey || '').trim();
  const actualGroupId = String(titledGroup?.groupId || titledGroup?.id || '').trim();
  if (!expectedSheetId
    || expectedSheetId !== String(sheet.id || '').trim()
    || !expectedEntryId
    || !expectedKey
    || String(group?.key || '').trim() !== expectedKey
    || !titledGroup
    || String(titledGroup.groupTitleEntryId || '').trim() !== expectedEntryId
    || String(titledGroup.drawingSheetId || '').trim() !== expectedSheetId
    || actualSystemId !== expectedSystemId
    || actualGroupId !== expectedGroupId
    || String(titledGroup.key || '').trim() !== expectedKey) {
    throw new Error('Die ausgewählte Gruppe wurde nicht gefunden.');
  }

  const updated = updateCoreGroupWorkflowStatus(
    sheet.coreGroupTitles,
    titledGroup.groupTitleEntryId,
    workflowStatus
  );
  if (!updated.changed) return updated.workflowStatus;

  sheet.coreGroupTitles = updated.entries;
  const reconciled = reconcileDrawingSheetCoreGroups(sheet, getCoreGroups(), envelope);
  sheet.sidebarGroups = summarizeSidebarGroups(reconciled.groups);
  S.drawingSheets = envelope;
  markDrawingSheetsChanged('core-group-workflow-status');
  AppRuntime.refreshChromeOnly('core-group-workflow-status');
  return updated.workflowStatus;
}

async function deleteDrawingSheet(sheetId) {
  const envelope = ensureDrawingSheets();
  const index = envelope.sheets.findIndex(sheet => sheet.id === sheetId);
  if (index < 0) throw new Error('Zeichnungsebene wurde nicht gefunden.');
  if (envelope.sheets.length <= 1) {
    throw new Error('Die letzte Zeichnungsebene kann nicht gelöscht werden.');
  }
  if (drawingSheetHasContent(sheetId)) {
    throw new Error('Die Zeichnungsebene kann erst gelöscht werden, wenn alle Zeichnungsinhalte entfernt sind.');
  }

  const deletingActiveSheet = envelope.activeSheetId === sheetId;
  const fallbackSheet = envelope.sheets[index + 1] || envelope.sheets[index - 1] || null;
  if (deletingActiveSheet && fallbackSheet) {
    const activated = await activateDrawingSheet(fallbackSheet.id, {
      captureCurrent: false,
      changeReason: 'drawing-sheet-delete'
    });
    if (!activated) throw new Error('Verbleibende Zeichnungsebene konnte nicht aktiviert werden.');
  }
  const currentEnvelope = ensureDrawingSheets();
  currentEnvelope.sheets = currentEnvelope.sheets.filter(sheet => sheet.id !== sheetId);
  currentEnvelope.sheets.forEach((sheet, sortOrder) => { sheet.sortOrder = sortOrder; });
  runtimeHistoryBySheetId.delete(sheetId);
  S.drawingSheets = currentEnvelope;
  if (!deletingActiveSheet) markDrawingSheetsChanged('drawing-sheet-delete');
  AppRuntime.refresh(false);
  return true;
}

function suggestedNewDrawingSheetTitle() {
  return suggestedDrawingSheetTitle(ensureDrawingSheets());
}

// Aendert ausschliesslich die Anzeige-/Darstellungsreihenfolge der
// Zeichnungsebenen (Sidebar, 3D-Modal, Drucken-Modal, BOM -- alle lesen
// dieselbe envelope.sheets-Reihenfolge bzw. sheet.sortOrder). Loest bewusst
// keine Zeichnungsebenen-Aktivierung, -Aenderung oder -Loeschung aus. Ist
// orderedSheetIds keine gueltige Permutation der vorhandenen Ebenen-IDs
// (falsche Laenge, unbekannte oder doppelte ID), bleibt die bisherige
// Reihenfolge unveraendert.
function reorderDrawingSheets(orderedSheetIds) {
  const envelope = ensureDrawingSheets();
  if (!Array.isArray(orderedSheetIds) || orderedSheetIds.length !== envelope.sheets.length) return false;
  const byId = new Map(envelope.sheets.map(sheet => [sheet.id, sheet]));
  const seen = new Set();
  const reordered = [];
  for (const id of orderedSheetIds) {
    const sheet = byId.get(id);
    if (!sheet || seen.has(id)) return false;
    seen.add(id);
    reordered.push(sheet);
  }
  const unchanged = reordered.every((sheet, index) => sheet === envelope.sheets[index]);
  if (unchanged) return true;
  reordered.forEach((sheet, sortOrder) => { sheet.sortOrder = sortOrder; });
  envelope.sheets = reordered;
  S.drawingSheets = envelope;
  markDrawingSheetsChanged('drawing-sheet-reorder');
  AppRuntime.refreshChromeOnly('drawing-sheet-reorder');
  return true;
}

export {
  MAX_DRAWING_SHEETS,
  SHARED_ORDER_PROJECT_KEYS,
  activateDrawingSheet,
  activeDrawingSheet,
  activeDrawingSheetCoreGroup,
  activeDrawingSheetCoreGroups,
  activeDrawingSheetPositionPrefix,
  anyDrawingSheetHasContent,
  createDrawingSheet,
  deleteDrawingSheet,
  drawingSheetHasContent,
  drawingSheetNavigationModel,
  drawingSheetProjects,
  ensureDrawingSheets,
  renameActiveCoreGroupTitleSuffix,
  renameDrawingSheet,
  reorderDrawingSheets,
  resetDrawingSheets,
  restoreDrawingSheetsFromDocument,
  serializeDrawingSheets,
  setActiveCoreGroupWorkflowStatus,
  suggestedNewDrawingSheetTitle,
  syncActiveDrawingSheetSidebarGroups
};
